import { useId, useState, type FormEvent } from 'react';
import { STATUS_REASON } from '@constants/limits';
import { CYLINDER_STATUS_ACTION, CYLINDER_STATUS_DIALOG as T } from '@locales/id/cylinders';
import { COMMON } from '@locales/id/messages';
import { errorText, setCylinderStatus, type Cylinder } from '@lib/api';
import { Banner, Button, Dialog, ErrorState, Field, useToast } from '@ui';
import { reasonProblem, type ManualStatus } from '@features/cylinders/model/status';

/** What the dialog reads of the cylinder (`testState` only matters when it is made active again). */
export type StatusSubject = Pick<Cylinder, 'id' | 'serialNo' | 'status'> & Partial<Pick<Cylinder, 'testState'>>;

/**
 * One status change (Tandai rusak, Tarik dari peredaran, Nyatakan hilang, Aktifkan kembali): what it means for the
 * cylinder, then a required reason (kept in the activity log). A refusal (it was loaded or left meanwhile) stays in the dialog with the reason kept; success closes it with a toast.
 */
export function CylinderStatusDialog({ c, target, onCancel, onDone }: { c: StatusSubject; target: ManualStatus; onCancel: () => void; onDone: () => void }) {
  const A = CYLINDER_STATUS_ACTION[target];
  const formId = useId();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const problem = reasonProblem(reason);
  const activating = target === 'ACTIVE';

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (busy || problem) return;
    setBusy(true);
    setError(null);
    try {
      await setCylinderStatus(c.id, { status: target, reason: reason.trim() });
      toast(A.done(c.serialNo));
      onDone();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={A.title(c.serialNo)}
      onClose={onCancel}
      dismissible={!busy}
      footer={
        <>
          <Button onClick={onCancel} disabled={busy}>
            {COMMON.cancel}
          </Button>
          <Button variant={activating ? 'primary' : 'danger'} type="submit" form={formId} busy={busy}>
            {A.label}
          </Button>
        </>
      }
    >
      <form id={formId} className="form" onSubmit={submit} noValidate>
        <ErrorState error={error} />
        <Banner tone={activating ? 'info' : 'warn'}>
          {A.consequence}
          {activating && c.testState === 'overdue' && <> {T.testOverdue}</>}
        </Banner>
        <Field label={T.reason} help={T.reasonHelp(STATUS_REASON.min, STATUS_REASON.max)} error={touched ? problem : null} required>
          {(id, d) => (
            <textarea
              id={id}
              aria-describedby={d}
              aria-invalid={touched && !!problem}
              aria-required
              rows={3}
              maxLength={STATUS_REASON.max}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onBlur={() => reason && setTouched(true)}
              data-autofocus
            />
          )}
        </Field>
      </form>
    </Dialog>
  );
}
